import { lazy, Suspense, useContext, useEffect } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import AuthWrapper from './components/wrappers/AuthWrapper'
import ErrorBoundary from './components/common/ErrorBoundary'
import RouteFallback from './components/common/RouteFallback'
import RoleGuard from './components/common/RoleGuard'
import { ROLES } from './constants/roles'

import UserWrapper from './components/wrappers/UserWrapper'
import { AuthContext, useAuth } from './hooks/auth/useAuth'
import Loading from './pages/Loading'
import OrganizationPage from './pages/user/Organization/OrganizationPage'

const HomePage = lazy(() => import('./pages/user/HomePage/HomePage'))
const PayrollPage = lazy(() => import('./pages/user/Payroll/PayrollPage'))
const EvaluationsPage = lazy(() => import('./pages/user/Evaluations/EvaluationsPage'))
const PlanningPage = lazy(() => import('./pages/user/Planning/PlanningPage'))
const UserWorkforcePage = lazy(() => import('./pages/user/Workforce/UserWorkforce'))
const InflowPage = lazy(() => import('./pages/user/Inflow/InflowPage'))
const ClassificationPage = lazy(() => import('./pages/user/Classification/ClassificationPage'))
const SettingsPage = lazy(() => import('./pages/user/Settings/SettingsPage'))

const AboutPeople = lazy(() => import('./pages/user/Workforce/AboutPeople'))
const Login = lazy(() => import('./pages/auth/LoginPage'))
const Register = lazy(() => import('./pages/auth/RegisterPage'))
const ForgotPass = lazy(() => import('./pages/auth/ForgotPage'))
const Verification = lazy(() => import('./pages/auth/VerificationPage'))
const ResetPass = lazy(() => import('./pages/auth/ResetPage'))
const ResetSuccess = lazy(() => import('./pages/auth/ResetSuccess'))
const InvitePeople = lazy(() => import('./pages/auth/InvitePage'))
const ProfilePage = lazy(() => import('./pages/user/Profile/ProfilePage'))
const AboutOrganization = lazy(() => import('./components/tables/AboutOrgTable'))
const AboutTeam = lazy(() => import('./pages/user/Workforce/AboutTeam'))
const AboutJob = lazy(() => import('./pages/user/Classification/AboutJob'))
const AboutSkills = lazy(() => import('./pages/user/Classification/AboutSkills'))
const AboutVacancy = lazy(() => import('./pages/user/Inflow/AboutVacancy'))
const UserPathPage = lazy(() => import('./pages/user/Paths/UserPathPage'))
const AddPathPage = lazy(() => import('./pages/user/Paths/AddPaths'))
const AboutContent = lazy(() => import('./pages/user/Content/aboutContent'))
const QuestionnaireResponses = lazy(() => import('./pages/user/Content/QuestionnaireResponses'))
const ContentEditorPage = lazy(() => import('./pages/user/Content/ContentEditorPage'))
const ContentPlayer = lazy(() => import('./pages/user/Content/ContentPlayer'))
const LegacyContentAuthoringRedirect = lazy(() => import('./pages/user/Content/LegacyContentAuthoringRedirect'))
const ExplorePage = lazy(() => import('./pages/user/Explore/ExplorePage'))
const AssignPage = lazy(() => import('./pages/user/Assign/AssignPage'))
const LibraryPage = lazy(() => import('./pages/user/Library/LibraryPage'))
const AssessmentsPage = lazy(() => import('./pages/user/Assessments/AssessmentsPage'))
const TakeAssessmentPage = lazy(() => import('./pages/user/Assessments/TakeAssessmentPage'))
const AssessmentReportPage = lazy(() => import('./pages/user/Assessments/AssessmentReportPage'))
const AssessmentIntakePage = lazy(() => import('./pages/public/AssessmentIntakePage'))
const TicketsPage = lazy(() => import('./pages/user/Tickets/TicketsPage'))
const ExpensesPage = lazy(() => import('./pages/user/Expenses/ExpensesPage'))
const TodoPage = lazy(() => import('./pages/user/Todo/TodoPage'))
const RoleDetail = lazy(() => import('./pages/user/Settings/RoleDetail'))
const EvaluationQuestions = lazy(() => import('./pages/user/Evaluations/EvaluationQuestions'))
const EvaluationCycleSettingsPage = lazy(() => import('./pages/user/Evaluations/EvaluationCycleSettingsPage'))
const DocumentationPage = lazy(() => import('./pages/user/Documentation/DocumentationPage'))
const MfaPage = lazy(() => import('./pages/auth/MfaPage'))
const MfaResetPage = lazy(() => import('./pages/auth/MfaResetPage'))
const EmailVerificationPage = lazy(() => import('./pages/auth/EmailVerificationPage'))
const SelectPlanPage = lazy(() => import('./pages/auth/SelectPlanPage'))
const OAuthAuthorizePage = lazy(() => import('./pages/auth/OAuthAuthorizePage'))

const AboutCandidate = lazy(() => import('./pages/user/Inflow/AboutCandidate'))
const AboutActivity = lazy(() => import('./pages/user/TimeTracking/AboutActivity'))
const AIChatV2Page = lazy(() => import('./components/ai-chat-v2/AIChatV2Page'))
const HRChatPage = lazy(() => import('./components/hr-chat/HRChatPage'))
const HelpPage = lazy(() => import('./pages/user/Help/HelpPage'))
const FAQPage = lazy(() => import('./pages/user/FAQ/FAQPage'))
const FAQAdminPage = lazy(() => import('./pages/user/FAQ/FAQAdminPage'))
const AnalyticsPage = lazy(() => import('./pages/user/Analytics/AnalyticsPage'))
const OutflowPage = lazy(() => import('./pages/user/Outflow/OutflowPage'))
const ProjectDetailPage = lazy(() => import('./pages/user/TimeTracking/ProjectDetailPage'))



// Client-side role allow-lists for admin-only routes. These mirror which roles
// see each item in the sidebar (see Navbar.tsx). The backend is the source of
// truth and enforces permissions server-side; blocked roles go to /user/home.
const ADMIN_ONLY = [ROLES.SUPER_ADMIN, ROLES.ORG_ADMIN] // settings, classification, outflow, mutations, faq-manage
const ADMIN_WITH_FINANCE = [ROLES.SUPER_ADMIN, ROLES.ORG_ADMIN, ROLES.COUNTRY_ADMIN, ROLES.FINANCE] // payroll, analytics
const ADMIN_WITH_COUNTRY = [ROLES.SUPER_ADMIN, ROLES.ORG_ADMIN, ROLES.COUNTRY_ADMIN] // evaluations
const ALL_ADMIN_ROLES = [ROLES.SUPER_ADMIN, ROLES.ORG_ADMIN, ROLES.COUNTRY_ADMIN, ROLES.FINANCE, ROLES.TEAM_LEADER]

const PrivateUserRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated } = useContext(AuthContext)

  if (!isAuthenticated) return <Navigate to="/auth/login" />
  return (
    <UserWrapper>
      <Suspense fallback={<RouteFallback minH="60vh" />}>{children}</Suspense>
    </UserWrapper>
  )
}

const PublicAuthRoute = ({ children }: { children: React.ReactNode }) => {
  const auth = useContext(AuthContext)
  const currentPath = window.location.pathname
  const searchParams = new URLSearchParams(window.location.search)
  const mode = searchParams.get('mode')

  // Action-code pages must stay reachable even when another account is already
  // signed in in this browser; otherwise a valid reset link silently redirects
  // to the home page before Firebase can verify it.
  const isAllowedAuthPage =
    currentPath === '/auth/mfa' ||
    currentPath === '/auth/verification' ||
    currentPath === '/auth/verify-email' ||
    currentPath === '/auth/reset-mfa' ||
    currentPath === '/auth/register' || // TEMP: Allow registration page for testing
    currentPath === '/auth/select-plan' ||
    currentPath === '/auth/oauth-authorize' || // MCP OAuth consent — must stay reachable even when signed in
    (currentPath === '/auth/resetpass' && ['verifyEmail', 'revertSecondFactorAddition', 'resetPassword'].includes(mode || ''))

  // If user is authenticated and verified, check if MFA is required (same as login: only when not yet completed this session)
  if (auth.isAuthenticated && auth.isVerified && !isAllowedAuthPage) {
    const mustCompleteMFA = auth.requiresMFA || (auth.mfaRequiredForUser && !auth.mfaVerifiedThisSession)
    if (mustCompleteMFA) {
      return <Navigate to="/auth/mfa" replace />
    }
    return <Navigate to="/user/home" replace />
  }

  return (
    <Suspense fallback={<Loading />}>
      {currentPath === '/auth/select-plan' ? children : <AuthWrapper>{children}</AuthWrapper>}
    </Suspense>
  )
}

const App = () => {
  const auth = useAuth()
  const { i18n } = useTranslation()

  // Change language based on user preference
  useEffect(() => {
    if (auth.user?.language && i18n.language !== auth.user.language) {
      i18n.changeLanguage(auth.user.language)
    }
  }, [auth.user?.language, i18n])

  if (auth.loading.query) return <Loading />
  return (
    <AuthContext.Provider value={auth}>
      <BrowserRouter>
        <Routes>
          
          <Route
            path="/auth/*"
            element={
              <PublicAuthRoute>
                <Routes>
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/forgotpass" element={<ForgotPass />} />
                  <Route path="/verification" element={<Verification />} />
                  <Route path="/resetpass" element={<ResetPass />} />
                  <Route path="/resetsuccess" element={<ResetSuccess />} />
                  <Route path="/invite" element={<InvitePeople />} />
                  <Route path="/mfa" element={<MfaPage />} />
                  <Route path="/verify-email" element={<EmailVerificationPage />} />
                  <Route path="/reset-mfa" element={<MfaResetPage />} />
                  <Route path="/select-plan" element={<SelectPlanPage />} />
                  <Route path="/oauth-authorize" element={<OAuthAuthorizePage />} />
                </Routes>
              </PublicAuthRoute>
            }
          />
          <Route
            path="/user/*"
            element={
              <ErrorBoundary>
                <PrivateUserRoute>
                <Routes>
                  <Route path="/home" element={<HomePage />} />
                  <Route path="/home/:id" element={<HomePage />} />
                  <Route path="/classification/jobprofile" element={<RoleGuard allow={ADMIN_ONLY}><ClassificationPage selectedTab="jobprofile" /></RoleGuard>} />
                  <Route path="/classification/skills" element={<RoleGuard allow={ADMIN_ONLY}><ClassificationPage selectedTab="skills" /></RoleGuard>} />
                  <Route path="/classification/competences" element={<RoleGuard allow={ADMIN_ONLY}><ClassificationPage selectedTab="competences" /></RoleGuard>} />
                  <Route path="/classification/tasks" element={<RoleGuard allow={ADMIN_ONLY}><ClassificationPage selectedTab="tasks" /></RoleGuard>} />
                  <Route path="/classification/roles" element={<RoleGuard allow={ADMIN_ONLY}><ClassificationPage selectedTab="roles" /></RoleGuard>} />
                  <Route path="/classification/accountability" element={<RoleGuard allow={ADMIN_ONLY}><ClassificationPage selectedTab="accountability" /></RoleGuard>} />
                  <Route path="classification/jobprofile/:id" element={<RoleGuard allow={ADMIN_ONLY}><AboutJob /></RoleGuard>} />
                  <Route path="classification/skills/:id" element={<RoleGuard allow={ADMIN_ONLY}><AboutSkills /></RoleGuard>} />
                  <Route path="/workforce/people" element={<UserWorkforcePage selectedTab="people" />} />
                  <Route path="/workforce/people/:id" element={<AboutPeople />} />
                  <Route path="/workforce/teams/:id" element={<AboutTeam />} />
                  <Route path="/workforce/teams" element={<UserWorkforcePage selectedTab="teams" />} />
                  <Route path="/workforce/organization" element={<UserWorkforcePage selectedTab="organization" />} />
                  <Route path="/workforce/organization/:id" element={<AboutOrganization />} />
                  <Route path="/workforce/assets" element={<UserWorkforcePage selectedTab="assets" />} />
                  <Route path="/workforce/edithistory" element={<UserWorkforcePage selectedTab="edithistory" />} />
                  <Route
                    path="/workforce/mutations"
                    element={
                      <RoleGuard allow={ADMIN_WITH_FINANCE}>
                        <UserWorkforcePage selectedTab="mutations" />
                      </RoleGuard>
                    }
                  />
                  <Route path="/organization/organization" element={<OrganizationPage />} />  {/* MUST come before :id route */}
                  <Route path="/organization" element={<OrganizationPage />} />
                  <Route path="/organization/:id" element={<AboutTeam  />} />
                  <Route path="/organization/:type" element={<OrganizationPage />} />
                  <Route path="/profile" element={<ProfilePage />} />
                  {/* Documents merged into the Content section's Documents tab */}
                  <Route path="/document" element={<Navigate to="/user/library/documents" replace />} />
                  <Route path="/document/:type" element={<Navigate to="/user/library/documents" replace />} />
                  {/* Admin Document Flow/HR Info now in Content; Templates/Statistics now in Assign */}
                  <Route path="/admin/document/:type" element={<Navigate to="/user/library/documents" replace />} />
                  <Route path="/content" element={<Navigate to="/user/explore/content" replace />} />
                  <Route path="/content/:id" element={<AboutContent />} />
                  <Route path="/content/:id/take" element={<ContentPlayer />} />
                  <Route path="/content/new" element={<ContentEditorPage />} />
                  <Route path="/content/:id/edit" element={<ContentEditorPage />} />
                  <Route path="/content/:id/edit-items" element={<LegacyContentAuthoringRedirect />} />
                  <Route path="/content/:id/questionnaire" element={<LegacyContentAuthoringRedirect />} />
                  <Route path="/content/:id/questionnaire/responses/summary" element={<QuestionnaireResponses />} />
                  <Route path="/todo" element={<TodoPage />} />
                  <Route path="/questionnaire/create" element={<LegacyContentAuthoringRedirect />} />
                  <Route path="/questionnaire/addcontentitems" element={<LegacyContentAuthoringRedirect />} />  

                  {/* Paths list merged into the Content nav's Paths tab (same table); detail
                      pages stay put since both tables link into these :id routes. */}
                  <Route path="/explore/paths" element={<Navigate to="/user/library/paths" replace />} />
                  <Route path="/explore/paths/:id" element={<AddPathPage />} />

                  <Route path="/explore/userpath" element={<Navigate to="/user/library/paths" replace />} />
                  <Route path="/explore/userpath/:id" element={<UserPathPage />} />

                  <Route path="/explore/content" element={<ExplorePage />} />
                  {/* Library merged into the Content tab; keep old URL working */}
                  <Route path="/explore/library" element={<Navigate to="/user/explore/content" replace />} />
                  {/* Assign + Statistics moved out of Explore into their own Assign page */}
                  <Route path="/explore/assign" element={<Navigate to="/user/assign/document" replace />} />
                  <Route path="/explore/statistics" element={<Navigate to="/user/assign/statistics" replace />} />

                  <Route path="/assign/document" element={<RoleGuard allow={ALL_ADMIN_ROLES}><AssignPage selectedTab="document" /></RoleGuard>} />
                  <Route path="/assign/learning" element={<RoleGuard allow={ALL_ADMIN_ROLES}><AssignPage selectedTab="learning" /></RoleGuard>} />
                  <Route path="/assign/survey" element={<RoleGuard allow={ALL_ADMIN_ROLES}><AssignPage selectedTab="survey" /></RoleGuard>} />
                  <Route path="/assign/path" element={<RoleGuard allow={ALL_ADMIN_ROLES}><AssignPage selectedTab="path" /></RoleGuard>} />
                  <Route path="/assign/statistics" element={<RoleGuard allow={ALL_ADMIN_ROLES}><AssignPage selectedTab="statistics" /></RoleGuard>} />
                  <Route path="/assign" element={<RoleGuard allow={ALL_ADMIN_ROLES}><Navigate to="/user/assign/document" replace /></RoleGuard>} />

                  <Route path="/library/documents" element={<LibraryPage selectedTab="documents" />} />
                  <Route path="/library/learning" element={<LibraryPage selectedTab="learning" />} />
                  <Route path="/library/surveys" element={<LibraryPage selectedTab="surveys" />} />
                  <Route path="/library/content" element={<LibraryPage selectedTab="content" />} />
                  <Route path="/library/paths" element={<LibraryPage selectedTab="paths" />} />
                  <Route path="/library" element={<Navigate to="/user/library/documents" replace />} />

                  {/* Assessments has its own place in the Manage nav; the old
                      Explore sub-tab URL keeps working for existing links. */}
                  <Route path="/assessments" element={<AssessmentsPage />} />
                  <Route path="/explore/assessments" element={<Navigate to="/user/assessments" replace />} />
                  <Route path="/assessments/:id/take" element={<TakeAssessmentPage />} />
                  <Route path="/assessments/:id/report" element={<AssessmentReportPage />} />

                  <Route path="/tickets" element={<RoleGuard allow={ADMIN_ONLY}><TicketsPage /></RoleGuard>} />
                  <Route path="/expenses" element={<ExpensesPage />} />

                  <Route path="/payroll/people" element={<RoleGuard allow={ADMIN_WITH_FINANCE}><PayrollPage selectedTab="people" /></RoleGuard>} />
                  <Route path="/payroll/working-days" element={<RoleGuard allow={ADMIN_WITH_FINANCE}><PayrollPage selectedTab="working-days" /></RoleGuard>} />
                  <Route path="/payroll/benefits" element={<RoleGuard allow={ADMIN_WITH_FINANCE}><PayrollPage selectedTab="benefits" /></RoleGuard>} />
                  {/* Benefit Mutations merged into Workforce's Mutation tab */}
                  <Route path="/payroll/benefit-mutations" element={<Navigate to="/user/workforce/mutations" replace />} />

                  <Route path="/evaluations" element={<RoleGuard allow={ADMIN_WITH_COUNTRY}><EvaluationsPage/></RoleGuard>} />
                  <Route path="/evaluations/questions" element={<RoleGuard allow={ADMIN_WITH_COUNTRY}><EvaluationQuestions /></RoleGuard>} />
                  <Route path="/evaluations/cycle-settings" element={<RoleGuard allow={ADMIN_WITH_COUNTRY}><EvaluationCycleSettingsPage /></RoleGuard>} />

                  {/* Settings routes are gated to org-level admins client-side (backend enforces). */}
                  <Route path="/settings" element={<RoleGuard allow={ADMIN_ONLY}><Navigate to="/user/settings/general" replace /></RoleGuard>} />
                  <Route path="/settings/general" element={<RoleGuard allow={ADMIN_ONLY}><SettingsPage selectedTab="general" /></RoleGuard>} />
                  <Route path="/settings/integration" element={<RoleGuard allow={ADMIN_ONLY}><SettingsPage selectedTab="integration" /></RoleGuard>} />
                  <Route path="/settings/access" element={<RoleGuard allow={ADMIN_ONLY}><SettingsPage selectedTab="access" /></RoleGuard>} />
                  <Route path="/settings/access/roles/:id" element={<RoleGuard allow={ADMIN_ONLY}><RoleDetail /></RoleGuard>} />
                  <Route path="/settings/payments" element={<RoleGuard allow={ADMIN_ONLY}><SettingsPage selectedTab="payments" /></RoleGuard>} />
                  <Route path="/settings/modules" element={<RoleGuard allow={ADMIN_ONLY}><SettingsPage selectedTab="modules" /></RoleGuard>} />
                  <Route path="/settings/assessments" element={<RoleGuard allow={ADMIN_ONLY}><SettingsPage selectedTab="assessments" /></RoleGuard>} />
                  <Route path="/settings/organizations" element={<RoleGuard allow={ADMIN_ONLY}><SettingsPage selectedTab="organizations" /></RoleGuard>} />
                  <Route path="/settings/plans" element={<RoleGuard allow={ADMIN_ONLY}><SettingsPage selectedTab="plans" /></RoleGuard>} />

                  <Route path="/planning/attendance-calendar" element={<PlanningPage selectedTab="attendance-calendar" />} />
                  <Route path="/planning/past-requests" element={<PlanningPage selectedTab="past-requests" />} />
                  <Route path="/planning/attendance" element={<PlanningPage selectedTab="attendance" />} />
                  <Route path="/planning/leave-management" element={<PlanningPage selectedTab="leave-management" />} />
                  <Route path="/planning/workspace" element={<PlanningPage selectedTab="workspace" />} />
                  <Route path="/planning/timeline" element={<PlanningPage selectedTab="timeline" />} />
                  <Route path="/planning/desks" element={<PlanningPage selectedTab="desks" />} />
                  <Route path="/planning/rooms" element={<PlanningPage selectedTab="rooms" />} />
                  <Route path="/planning/offices" element={<PlanningPage selectedTab="offices" />} />
                  <Route path="/planning" element={<Navigate to="/user/planning/attendance-calendar" replace />} />
                  <Route path="/calendar" element={<Navigate to="/user/planning/attendance-calendar" replace />} />
                  <Route path="/calendar/admin" element={<Navigate to="/user/planning/attendance-calendar" replace />} />
                  {/* Old planning sub-routes now handled by unified PlanningPage */}

                  <Route path="/inflow/candidates" element={<InflowPage selectedTab="candidates" />} />
                  <Route path="/inflow/candidates/:id" element={<AboutCandidate />} />
                  <Route path="/inflow/pipeline" element={<InflowPage selectedTab="pipeline" />} />
                  <Route path="/inflow/interviews" element={<InflowPage selectedTab="interviews" />} />
                  <Route path="/inflow/vacancy" element={<InflowPage selectedTab="vacancy" />} />
                  <Route path="/inflow/vacancy/:id" element={<AboutVacancy />} />
                  <Route path="/inflow/overview" element={<InflowPage selectedTab="overview" />} />
                  <Route path="/inflow/onboarding" element={<InflowPage selectedTab="onboarding" />} />
                  <Route path="/inflow" element={<Navigate to="/user/inflow/candidates" replace />} />

                  <Route path="/recruitment/reports" element={<Navigate to="/user/analytics/recruitment" replace />} />
                  <Route path="/recruitment/*" element={<Navigate to="/user/inflow/candidates" replace />} />

                   <Route path="/documentation" element={<DocumentationPage />} />

                  <Route path="/chat" element={<AIChatV2Page />} />
                  <Route path="/chat-v2" element={<Navigate to="/user/chat" replace />} />
                  <Route path="/help" element={<HelpPage />} />
                  <Route path="/hr-chat" element={<HRChatPage />} />

                  <Route path="/faq" element={<FAQPage />} />
                  <Route path="/faq/manage" element={<RoleGuard allow={ADMIN_ONLY}><FAQAdminPage /></RoleGuard>} />

                  <Route path="/analytics/workforce" element={<RoleGuard allow={ADMIN_WITH_FINANCE}><AnalyticsPage selectedTab="workforce" /></RoleGuard>} />
                  <Route path="/analytics/attendance" element={<RoleGuard allow={ADMIN_WITH_FINANCE}><AnalyticsPage selectedTab="attendance" /></RoleGuard>} />
                  <Route path="/analytics/evaluation" element={<RoleGuard allow={ADMIN_WITH_FINANCE}><AnalyticsPage selectedTab="evaluation" /></RoleGuard>} />
                  <Route path="/analytics/skills" element={<RoleGuard allow={ADMIN_WITH_FINANCE}><AnalyticsPage selectedTab="skills" /></RoleGuard>} />
                  <Route path="/analytics/compensation" element={<RoleGuard allow={ADMIN_WITH_FINANCE}><AnalyticsPage selectedTab="compensation" /></RoleGuard>} />
                  <Route path="/analytics/recruitment" element={<RoleGuard allow={ADMIN_WITH_FINANCE}><AnalyticsPage selectedTab="recruitment" /></RoleGuard>} />
                  <Route path="/analytics/turnover" element={<RoleGuard allow={ADMIN_WITH_FINANCE}><AnalyticsPage selectedTab="turnover" /></RoleGuard>} />
                  <Route path="/analytics" element={<RoleGuard allow={ADMIN_WITH_FINANCE}><Navigate to="/user/analytics/workforce" replace /></RoleGuard>} />

                  <Route path="/outflow/offboarding" element={<RoleGuard allow={ADMIN_ONLY}><OutflowPage selectedTab="offboarding" /></RoleGuard>} />
                  <Route path="/outflow/exit-interviews" element={<RoleGuard allow={ADMIN_ONLY}><OutflowPage selectedTab="exit-interviews" /></RoleGuard>} />
                  <Route path="/outflow/outstanding-assets" element={<RoleGuard allow={ADMIN_ONLY}><OutflowPage selectedTab="outstanding-assets" /></RoleGuard>} />
                  <Route path="/outflow" element={<RoleGuard allow={ADMIN_ONLY}><Navigate to="/user/outflow/offboarding" replace /></RoleGuard>} />

                  <Route path="/outflow/exit-management" element={<Navigate to="/user/outflow/offboarding" replace />} />
                  <Route path="/outflow/turnover-reports" element={<Navigate to="/user/analytics/turnover" replace />} />

                  <Route path="/planning/time-tracking" element={<PlanningPage selectedTab="time-tracking" />} />
                  <Route path="/planning/time-tracking/projects/:id" element={<ProjectDetailPage />} />
                  <Route path="/planning/time-tracking/activities/:id" element={<AboutActivity />} />

                </Routes>
              </PrivateUserRoute>
              </ErrorBoundary>
            }
          />
          <Route
            path="/assessment-intake/:accessToken"
            element={
              <Suspense fallback={<Loading />}>
                <AssessmentIntakePage />
              </Suspense>
            }
          />
          <Route path="/" element={<Navigate to="/auth/login" />} />
        </Routes>
      </BrowserRouter>
    </AuthContext.Provider>
  )
}

export default App
